import { useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2, FolderOpen, Route, FileDown, Lightbulb, Mail, Loader2 } from "lucide-react";
import { fmtMoney } from "@/lib/pricing";
import { QUOTE_STATUSES } from "@/components/SaveQuoteModal";
import api, { API } from "@/lib/api";
import { toast } from "sonner";

const EMAIL_RE = /^\S+@\S+\.\S+$/;

const STATUS_CLS = {
  Draft: "bg-stone-100 text-stone-700 border-stone-300",
  Sent: "bg-orange-50 text-[#EA580C] border-[#F97316]/40",
  Accepted: "bg-green-50 text-green-700 border-green-300",
  Declined: "bg-red-50 text-red-700 border-red-300",
};

const nameOrDash = (...candidates) => {
  for (const c of candidates) {
    const v = (c || "").trim();
    if (v) return v;
  }
  return "—";
};

// One saved-quote row in the history dialog.
export const QuoteRow = ({ q, onStatusChange, onReopen, onDelete }) => {
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  const sendQuoteEmail = async () => {
    const emails = emailTo.split(",").map((e) => e.trim()).filter((e) => EMAIL_RE.test(e));
    if (!emails.length) {
      toast.error("Enter a valid email address");
      return;
    }
    setSendingEmail(true);
    try {
      const { data } = await api.post(`/quotes/${q.id}/email`, { recipient_emails: emails });
      toast.success(data.message);
      setEmailOpen(false);
      setEmailTo("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to email quote");
    } finally {
      setSendingEmail(false);
    }
  };

  return (
  <div className="border border-border rounded-lg p-3 space-y-2" data-testid={`quote-row-${q.id}`}>
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#1C1917] truncate">
          <Route className="h-4 w-4 text-[#F97316] shrink-0" />
          <span className="truncate" data-testid={`quote-client-${q.id}`}>
            {q.client_name || q.route_summary}
          </span>
          <span
            className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border shrink-0 ${STATUS_CLS[q.status] || STATUS_CLS.Draft}`}
            data-testid={`quote-status-badge-${q.id}`}
          >
            {q.status || "Draft"}
          </span>
        </div>
        <div className="text-xs text-[#57534E] mt-1" data-testid={`quote-meta-${q.id}`}>
          <span>Client: <span className="font-medium text-[#1C1917]">{nameOrDash(q.client_name, q.data?.customers?.[0]?.name, q.data?.customer?.name)}</span></span>
          <span> · Agent: <span className="font-medium text-[#1C1917]">{nameOrDash(q.agent_name, q.data?.salesAgent?.name)}</span></span>
          {q.mower_type && <span> · {q.mower_type}</span>}
        </div>
        <div className="text-xs text-[#57534E] mt-0.5 space-x-1">
          <span className="font-semibold text-[#EA580C]">{q.chosen_method}</span>
          <span>· {fmtMoney(q.final_price)} shipping · {q.total_miles} mi</span>
          {Number(q.mower_cost) > 0 && (
            <span data-testid={`quote-total-${q.id}`}>
              · Total w/ mower: <span className="font-semibold text-[#1C1917]">{fmtMoney(Number(q.mower_cost) + Number(q.final_price))}</span>
            </span>
          )}
        </div>
        <div className="text-xs text-stone-400 mt-0.5">
          {q.client_name ? `${q.route_summary} · ` : ""}
          {new Date(q.created_at).toLocaleString()}
        </div>
        {q.route_addon_note && (
          <p className="flex items-start gap-1.5 text-xs text-[#EA580C] mt-1.5 bg-[#FFF7ED] border border-[#F97316]/30 rounded px-2 py-1" data-testid={`quote-addon-${q.id}`}>
            <Lightbulb className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span>Route add-on: {q.route_addon_note}</span>
          </p>
        )}
      </div>
      <Button variant="ghost" size="icon" className="text-red-600 shrink-0" onClick={() => onDelete(q.id)} data-testid={`delete-quote-${q.id}`}>
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
    <div className="flex flex-wrap items-center gap-2">
      <Select value={q.status || "Draft"} onValueChange={(v) => onStatusChange(q.id, v)}>
        <SelectTrigger className="h-8 w-[120px] text-xs" data-testid={`quote-status-select-${q.id}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {QUOTE_STATUSES.map((s) => (
            <SelectItem key={s} value={s}>{s}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {q.pdf_id && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => window.open(`${API}/rfp-pdf/${q.pdf_id}`, "_blank")}
          data-testid={`quote-pdf-${q.id}`}
        >
          <FileDown className="h-4 w-4 sm:mr-1.5" />
          <span className="hidden sm:inline">PDF Quote</span>
        </Button>
      )}
      <Button variant="outline" size="sm" onClick={() => onReopen(q)} data-testid={`reopen-quote-${q.id}`}>
        <FolderOpen className="h-4 w-4 sm:mr-1.5" />
        <span className="hidden sm:inline">Reopen</span>
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setEmailOpen((v) => !v)}
        data-testid={`email-quote-${q.id}`}
      >
        <Mail className="h-4 w-4 sm:mr-1.5" />
        <span className="hidden sm:inline">Email</span>
      </Button>
    </div>
    {emailOpen && (
      <div className="flex items-center gap-2" data-testid={`email-quote-form-${q.id}`}>
        <Input
          type="text"
          placeholder="recipient@example.com (comma-separate for more)"
          value={emailTo}
          onChange={(e) => setEmailTo(e.target.value)}
          className="flex-1 h-8 text-sm focus-visible:ring-2 focus-visible:ring-[#F97316]/30"
          data-testid={`email-quote-input-${q.id}`}
        />
        <Button
          size="sm"
          onClick={sendQuoteEmail}
          disabled={sendingEmail}
          className="bg-[#F97316] hover:bg-[#EA580C] text-white h-8"
          data-testid={`email-quote-send-${q.id}`}
        >
          {sendingEmail ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
          <span className="ml-1">Send</span>
        </Button>
      </div>
    )}
  </div>
  );
};
